import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { STICKERS, CATEGORIES } from "../data/stickers";
import { useStore } from "../context/StoreContext";
import usePageMeta from "../hooks/usePageMeta";

const Stickers = () => {
  const { stickers, name } = useStore();
  const owned = new Set(stickers);
  const ownedCount = stickers.length;
  const total = STICKERS.length;
  const [tab, setTab] = useState("all");

  usePageMeta(
    "Sticker Book · 67Cafe",
    "Collect shiny stickers on 67Cafe by playing games, keeping streaks, and shopping — see your whole collection here."
  );

  const filtered = tab === "all" ? STICKERS : STICKERS.filter((s) => s.category === tab);

  return (
    <div
      className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8"
      data-testid="stickers-page"
    >
      <Link
        to="/shop"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="stickers-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={3} /> Back
      </Link>

      <div
        className="chunky-card p-5 sm:p-6 mb-6"
        style={{ background: "linear-gradient(135deg,#FFE0F5 0%,#FFF9E6 100%)" }}
      >
        <div
          className="inline-flex items-center gap-2 text-sm font-display"
          style={{ color: "#F368E0" }}
        >
          <Sparkles size={16} strokeWidth={3} /> Sticker Book
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl leading-tight mt-1"
          data-testid="stickers-title"
        >
          {name || "Kid"}&#39;s shiny stickers ✨
        </h1>
        <p className="mt-2 text-base" style={{ color: "#484848" }}>
          <strong data-testid="stickers-count">
            {ownedCount} / {total}
          </strong>{" "}
          collected. Play games, keep streaks, and shop to earn more!
        </p>
      </div>

      {/* Category tabs */}
      <div
        className="flex flex-wrap gap-2 mb-5"
        data-testid="stickers-tabs"
      >
        {[{ id: "all", name: "All", emoji: "✨" }, ...CATEGORIES].map((c) => {
          const active = tab === c.id;
          const count =
            c.id === "all"
              ? STICKERS.length
              : STICKERS.filter((s) => s.category === c.id).length;
          const ownedInCat =
            c.id === "all"
              ? ownedCount
              : STICKERS.filter((s) => s.category === c.id && owned.has(s.id)).length;
          return (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              data-testid={`stickers-tab-${c.id}`}
              className="rounded-full px-3 py-1.5 font-display text-sm"
              style={{
                background: active ? "#F368E0" : "#fff",
                color: active ? "#fff" : "#484848",
                border: "3px solid #2b2b2b",
                boxShadow: active ? "0 3px 0 #2b2b2b" : "0 2px 0 #2b2b2b",
              }}
            >
              <span className="mr-1" aria-hidden="true">{c.emoji}</span>
              {c.name} · {ownedInCat}/{count}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
        {filtered.map((s) => {
          const isOwned = owned.has(s.id);
          return (
            <div
              key={s.id}
              data-testid={`sticker-${s.id}`}
              data-owned={isOwned ? "true" : "false"}
              data-category={s.category}
              className="chunky-card p-4 text-center relative overflow-hidden"
              style={{
                background: isOwned ? "#fff" : "#f3f3f3",
                filter: isOwned ? "none" : "grayscale(1)",
                opacity: isOwned ? 1 : 0.55,
              }}
            >
              <div
                className="mx-auto rounded-full flex items-center justify-center mb-2"
                style={{
                  width: 72,
                  height: 72,
                  background: isOwned ? s.color : "#c8c8c8",
                  border: "4px solid #2b2b2b",
                  boxShadow: "0 4px 0 #2b2b2b",
                }}
              >
                <span
                  className={isOwned ? "text-4xl bob" : "text-4xl"}
                  aria-hidden="true"
                >
                  {isOwned ? s.emoji : "🔒"}
                </span>
              </div>
              <div className="font-display text-base leading-tight">
                {s.name}
              </div>
              <p className="text-xs mt-1" style={{ color: "#484848" }}>
                {s.description}
              </p>
              {isOwned && (
                <span
                  className="absolute top-2 right-2 text-xs font-display px-2 py-0.5 rounded-full"
                  style={{
                    background: "#00A699",
                    color: "#fff",
                    border: "2px solid #2b2b2b",
                  }}
                >
                  ✓
                </span>
              )}
              {(s.category === "rare" || s.category === "weekly") && (
                <span
                  className="absolute top-2 left-2 text-[10px] font-display px-2 py-0.5 rounded-full"
                  style={{
                    background: s.category === "rare" ? "#FFB400" : "#54A0FF",
                    color: "#fff",
                    border: "2px solid #2b2b2b",
                  }}
                >
                  {s.category === "rare" ? "RARE" : "WEEKLY"}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Stickers;
